import { jsPDF } from 'jspdf'

/*
** Génération du programme hebdomadaire des animations en PDF.
** A4 paysage, une colonne par jour, une page FR + une page EN.
** Couleurs issues du thème du camping, logo en en-tête si disponible.
*/

const LOCALES = {
	fr: {
		title: "Programme des animations",
		weekdays: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"],
		months: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
		empty: "Pas d'animation",
		range: (d1, d2, months) => {
			if (d1.getMonth() === d2.getMonth())
				return "Du " + d1.getDate() + " au " + d2.getDate() + " " + months[d2.getMonth()] + " " + d2.getFullYear()
			return "Du " + d1.getDate() + " " + months[d1.getMonth()] + " au " + d2.getDate() + " " + months[d2.getMonth()] + " " + d2.getFullYear()
		}
	},
	en: {
		title: "Activity programme",
		weekdays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
		months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
		empty: "No activity",
		range: (d1, d2, months) => {
			if (d1.getMonth() === d2.getMonth())
				return months[d2.getMonth()] + " " + d1.getDate() + " - " + d2.getDate() + ", " + d2.getFullYear()
			return months[d1.getMonth()] + " " + d1.getDate() + " - " + months[d2.getMonth()] + " " + d2.getDate() + ", " + d2.getFullYear()
		}
	}
}

function hexToRgb(hex)
{
	const fallback = [79, 70, 229]
	if (!hex || typeof hex !== "string")
		return fallback
	const h = (hex.charAt(0) === '#') ? hex.substring(1) : hex
	if (h.length !== 6)
		return fallback
	return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)]
}

function cleanText(text)
{
	if (!text || typeof text !== "string")
		return ""
	// les polices standard de jsPDF (WinAnsi) ne couvrent ni les emojis ni les caractères hors latin-1
	return text
		.replace(/œ/g, "oe")
		.replace(/Œ/g, "OE")
		.replace(/\u00A0/g, ' ')
		.replace(/[^\u0020-\u007E\u00A1-\u00FF\u20AC]/g, '')
		.replace(/\s+/g, ' ')
		.trim()
}

function getTranslation(translations, lang)
{
	let fr = null
	if (!translations || translations.length === 0)
		return null
	for (let i = 0; i < translations.length; i++)
	{
		if (translations[i].lang === lang)
			return translations[i]
		if (translations[i].lang === 'fr')
			fr = translations[i]
	}
	return (fr) ? fr : translations[0]
}

function formatTime(timestamp, lang)
{
	const date = new Date(timestamp * 1000)
	const hours = date.getHours()
	let minutes = date.getMinutes()
	if (lang === 'en')
	{
		if (minutes < 10)
			minutes = '0' + minutes
		return hours + ":" + minutes
	}
	if (minutes === 0)
		return hours + "h"
	if (minutes < 10)
		minutes = '0' + minutes
	return hours + "h" + minutes
}

function loadLogo(logo)
{
	return new Promise(resolve => {
		if (!logo || typeof logo !== "string" || logo.indexOf("svg") !== -1)
			return resolve(null)
		const finish = (dataurl) => {
			if (!dataurl || dataurl.indexOf("data:image/") !== 0 || dataurl.indexOf("svg") !== -1)
				return resolve(null)
			const img = new Image()
			img.onload = () => {
				const format = (dataurl.indexOf("data:image/jpeg") === 0 || dataurl.indexOf("data:image/jpg") === 0) ? 'JPEG' : 'PNG'
				resolve({ data: dataurl, width: img.width, height: img.height, format: format })
			}
			img.onerror = () => resolve(null)
			img.src = dataurl
		}
		if (logo.indexOf("data:") === 0)
			finish(logo)
		else
		{
			fetch(logo)
			.then(res => res.blob())
			.then(blob => {
				const reader = new FileReader()
				reader.onloadend = () => finish(reader.result)
				reader.readAsDataURL(blob)
			})
			.catch(() => resolve(null))
		}
	})
}

function _drawPage(doc, lang, monday, days, categories, theme, logo_img)
{
	const locale = LOCALES[lang]
	const page_w = 297
	const margin = 8
	const gap = 2
	const col_w = (page_w - margin * 2 - gap * 6) / 7
	const body_bottom = 192
	const color1 = hexToRgb((theme) ? theme.color1 : null)

	// bandeau d'en-tête
	doc.setFillColor(color1[0], color1[1], color1[2])
	doc.rect(0, 0, page_w, 24, 'F')
	if (logo_img)
	{
		const logo_h = 16
		let logo_w = logo_h * (logo_img.width / logo_img.height)
		if (logo_w > 40)
			logo_w = 40
		doc.addImage(logo_img.data, logo_img.format, margin, 4, logo_w, logo_h)
	}
	const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6)
	doc.setTextColor(255, 255, 255)
	doc.setFont('helvetica', 'bold')
	doc.setFontSize(16)
	doc.text(locale.title, page_w / 2, 11, { align: 'center' })
	doc.setFont('helvetica', 'normal')
	doc.setFontSize(10)
	doc.text(locale.range(monday, sunday, locale.months), page_w / 2, 18, { align: 'center' })

	// colonnes des jours
	for (let d = 0; d < 7; d++)
	{
		const x = margin + d * (col_w + gap)
		const day_date = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + d)
		let day_nb = day_date.getDate()
		if (day_nb < 10)
			day_nb = '0' + day_nb
		let month_nb = day_date.getMonth() + 1
		if (month_nb < 10)
			month_nb = '0' + month_nb

		doc.setFillColor(241, 241, 241)
		doc.rect(x, 30, col_w, 8, 'F')
		doc.setTextColor(color1[0], color1[1], color1[2])
		doc.setFont('helvetica', 'bold')
		doc.setFontSize(9)
		doc.text(locale.weekdays[d] + " " + day_nb + "/" + month_nb, x + col_w / 2, 35.2, { align: 'center' })

		let y = 42
		if (days[d].length === 0)
		{
			doc.setTextColor(150, 150, 150)
			doc.setFont('helvetica', 'italic')
			doc.setFontSize(8)
			doc.text(locale.empty, x + col_w / 2, y + 2, { align: 'center' })
			continue
		}
		for (let i = 0; i < days[d].length; i++)
		{
			const item = days[d][i]
			const translation = getTranslation(item.animation_obj.translations, lang)
			const name = cleanText((translation && translation.name) ? translation.name : "")
			doc.setFontSize(8)
			const lines = doc.splitTextToSize(name, col_w - 5)
			const block_h = 4 + lines.length * 3.5 + 2
			if (y + block_h > body_bottom)
			{
				doc.setTextColor(120, 120, 120)
				doc.setFont('helvetica', 'bold')
				doc.text("...", x + 2, y + 2)
				break
			}
			const cat = item.animation_obj.category_obj
			const cat_color = hexToRgb((cat) ? cat.color : "888888")
			doc.setFillColor(cat_color[0], cat_color[1], cat_color[2])
			doc.rect(x, y, 1.2, block_h - 1.5, 'F')
			doc.setTextColor(40, 40, 40)
			doc.setFont('helvetica', 'bold')
			doc.text(formatTime(item.start, lang) + " - " + formatTime(item.end, lang), x + 3, y + 3)
			doc.setFont('helvetica', 'normal')
			doc.setTextColor(70, 70, 70)
			doc.text(lines, x + 3, y + 7)
			y += block_h + 1.5
		}
	}

	// légende des catégories utilisées sur la semaine
	const used = []
	for (let d = 0; d < 7; d++)
	{
		for (let i = 0; i < days[d].length; i++)
		{
			const cat = days[d][i].animation_obj.category_obj
			if (cat && used.indexOf(cat) === -1)
				used.push(cat)
		}
	}
	let legend_x = margin
	doc.setFontSize(8)
	for (let i = 0; i < used.length; i++)
	{
		const translation = getTranslation(used[i].translations, lang)
		const name = cleanText((translation && translation.name) ? translation.name : "")
		const color = hexToRgb(used[i].color)
		doc.setFillColor(color[0], color[1], color[2])
		doc.rect(legend_x, 197, 3, 3, 'F')
		doc.setTextColor(70, 70, 70)
		doc.setFont('helvetica', 'normal')
		doc.text(name, legend_x + 4.5, 199.5)
		legend_x += 4.5 + doc.getTextWidth(name) + 6
	}
	doc.setTextColor(170, 170, 170)
	doc.setFontSize(7)
	doc.text("PocketCamp", page_w - margin, 199.5, { align: 'right' })
}

export function generatePlanningPDF(monday, dates, categories, theme, logo)
{
	return loadLogo(logo).then(logo_img => {
		const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

		// répartition des occurrences par jour de la semaine, triées par heure
		const days = [[], [], [], [], [], [], []]
		const monday_ts = Math.floor(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate()).getTime() / 1000)
		for (let i = 0; i < dates.length; i++)
		{
			const date = new Date(dates[i].start * 1000)
			const day_start = Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() / 1000)
			const index = Math.round((day_start - monday_ts) / 86400)
			if (index >= 0 && index < 7 && dates[i].animation_obj)
				days[index].push(dates[i])
		}
		for (let d = 0; d < 7; d++)
			days[d].sort((a, b) => a.start - b.start)

		const langs = ['fr', 'en']
		for (let p = 0; p < langs.length; p++)
		{
			if (p > 0)
				doc.addPage()
			_drawPage(doc, langs[p], monday, days, categories, theme, logo_img)
		}

		let day_nb = monday.getDate()
		if (day_nb < 10)
			day_nb = '0' + day_nb
		let month_nb = monday.getMonth() + 1
		if (month_nb < 10)
			month_nb = '0' + month_nb
		doc.save("programme-animations-" + day_nb + "-" + month_nb + "-" + monday.getFullYear() + ".pdf")
	})
}
